import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  Progress,
  Spinner,
  Td,
  Text,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { FaChartBar } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { getConsultantSkillsSummary } from "../../../api/consultantsApi";
import {
  DataTable,
  EmptyState,
  KpiCard,
  KpiStrip,
  StatusBadge,
} from "../../../components/Portal/ui";

const ConsultantSkillsTab = ({ consultantId, consultantName, consultantEmail }) => {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const { selectedBusinessUnitId } = useBusinessUnit();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEvalId, setExpandedEvalId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!consultantId) return;
    setLoading(true);
    try {
      const result = await getConsultantSkillsSummary(consultantId, {
        buId: selectedBusinessUnitId || undefined,
      });
      setData(result);
    } catch (e) {
      console.error("Failed to fetch skills summary", e);
    } finally {
      setLoading(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getScoreColor = (score) => {
    if (score == null) return "gray";
    if (score >= 80) return "green";
    if (score >= 60) return "blue";
    if (score >= 40) return "yellow";
    return "red";
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" py="40px">
        <Spinner color="portal.primary" />
      </Flex>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<FaChartBar />}
        title={t("pilotConsultants.detail.asapSkills.noData", {
          defaultValue: "No skills data available.",
        })}
      />
    );
  }

  const { evaluations, averageScore, evaluationCount } = data;

  const trendValue = (() => {
    if (evaluations.length < 2) return "-";
    const latest = evaluations[0]?.overallScore;
    const previous = evaluations[1]?.overallScore;
    if (latest != null && previous != null) {
      const diff = latest - previous;
      return diff > 0 ? `+${diff}` : `${diff}`;
    }
    return "-";
  })();

  const skillColumns = [
    {
      key: "name",
      label: t("pilotConsultants.detail.asapSkills.skillName", {
        defaultValue: "Skill",
      }),
    },
    {
      key: "score",
      label: t("pilotConsultants.detail.asapSkills.skillScore", {
        defaultValue: "Score",
      }),
      width: "70px",
      align: "right",
    },
    {
      key: "progress",
      label: t("pilotConsultants.detail.asapSkills.skillProgress", {
        defaultValue: "Progress",
      }),
      width: "40%",
    },
  ];

  return (
    <VStack align="stretch" spacing="14px">
      {/* KPI Summary */}
      <KpiStrip columns={{ base: 1, md: 3 }}>
        <KpiCard
          label={t("pilotConsultants.detail.asapSkills.evaluationCount", {
            defaultValue: "Evaluations",
          })}
          value={evaluationCount}
        />
        <KpiCard
          label={t("pilotConsultants.detail.asapSkills.averageScore", {
            defaultValue: "Average Score",
          })}
          value={averageScore != null ? `${averageScore}/100` : "-"}
        />
        <KpiCard
          label={t("pilotConsultants.detail.asapSkills.trend", {
            defaultValue: "Trend",
          })}
          value={trendValue}
        />
      </KpiStrip>

      {/* Navigate to SKILLS module */}
      <Flex justify="flex-end">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const params = new URLSearchParams();
            params.set("consultantId", consultantId);
            if (consultantName) params.set("consultantName", consultantName);
            if (consultantEmail) params.set("consultantEmail", consultantEmail);
            navigate(`/portal/asap-skills?${params.toString()}`);
          }}
        >
          {t("pilotConsultants.detail.asapSkills.goToSkills", {
            defaultValue: "Go to ASAP SKILLS",
          })}
        </Button>
      </Flex>

      {/* Evaluation History */}
      <Heading
        as="h3"
        fontSize="14px"
        letterSpacing="-0.02em"
        color="portal.text"
        sx={{ marginBottom: "0 !important" }}
      >
        {t("pilotConsultants.detail.asapSkills.evaluationHistory", {
          defaultValue: "Evaluation History",
        })}
      </Heading>

      {evaluations.length === 0 ? (
        <EmptyState
          icon={<FaChartBar />}
          title={t("pilotConsultants.detail.asapSkills.noEvaluations", {
            defaultValue: "No completed evaluations yet.",
          })}
        />
      ) : (
        <VStack align="stretch" spacing="8px" maxH="500px" overflowY="auto">
          {evaluations.map((ev) => {
            const isExpanded = expandedEvalId === ev.evaluationId;
            return (
              <Box
                key={ev.evaluationId}
                borderWidth="1px"
                borderColor={isExpanded ? "portal.primary" : "portal.line"}
                borderRadius="10px"
                bg="portal.panel"
                boxShadow="kpi"
                p="12px"
                cursor="pointer"
                transition="border-color 0.15s ease"
                _hover={{ bg: "portal.soft" }}
                onClick={() =>
                  setExpandedEvalId(isExpanded ? null : ev.evaluationId)
                }
              >
                <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
                  <Box minW={0}>
                    <Text
                      fontSize="13px"
                      fontWeight="700"
                      color="portal.text"
                      isTruncated
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {ev.candidateName || ev.evaluationId}
                    </Text>
                    <Text
                      fontSize="11px"
                      color="portal.muted"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {ev.date ? new Date(ev.date).toLocaleDateString() : "-"}
                    </Text>
                  </Box>
                  <StatusBadge
                    label={ev.overallScore != null ? `${ev.overallScore}/100` : "-"}
                    colorScheme={getScoreColor(ev.overallScore)}
                  />
                </Flex>

                {/* Expanded skill breakdown */}
                {isExpanded && (
                  <Box mt="12px">
                    {ev.commentary && (
                      <Text
                        fontSize="12px"
                        color="portal.muted"
                        mb="10px"
                        sx={{ marginBottom: "10px !important" }}
                      >
                        {ev.commentary}
                      </Text>
                    )}
                    {ev.skills && ev.skills.length > 0 ? (
                      <Box
                        borderWidth="1px"
                        borderColor="portal.line"
                        borderRadius="10px"
                        overflow="hidden"
                      >
                        <DataTable
                          columns={skillColumns}
                          rows={ev.skills}
                          renderRow={(skill, idx) => (
                            <Tr key={idx}>
                              <Td>{skill.name}</Td>
                              <Td textAlign="right">
                                {skill.score != null ? skill.score : "-"}
                              </Td>
                              <Td>
                                {skill.score != null && (
                                  <Progress
                                    value={skill.score}
                                    size="sm"
                                    colorScheme={getScoreColor(skill.score)}
                                    borderRadius="999px"
                                  />
                                )}
                              </Td>
                            </Tr>
                          )}
                        />
                      </Box>
                    ) : (
                      <Text
                        fontSize="12px"
                        color="portal.muted"
                        sx={{ marginBottom: "0 !important" }}
                      >
                        {t("pilotConsultants.detail.asapSkills.noSkillBreakdown", {
                          defaultValue: "No detailed skill breakdown available.",
                        })}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default ConsultantSkillsTab;
